import { FC, useEffect } from 'react';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { Controller, useForm } from 'react-hook-form';

import { CampaignDetail } from '@/apis/core';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  isRequired,
  isValidInput,
} from '../../validators';

interface ProgramDetailsValues {
  description: string;
  expectedMessageVolume: string;
}

interface ProgramDetailsFormProps {
  campaign: CampaignDetail;
  onCancel: () => void;
  onSave: (data: {
    description: string;
    expectedMessageVolume: number;
  }) => void;
  processing?: boolean;
}

const ProgramDetailsForm: FC<ProgramDetailsFormProps> = ({
  campaign,
  onCancel,
  onSave,
  processing,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm<ProgramDetailsValues>({
    mode: 'onChange',
    defaultValues: {
      description: campaign?.description || '',
      expectedMessageVolume: campaign?.expectedMessageVolume?.toString() || '',
    },
  });

  useEffect(() => {
    if (campaign) {
      reset({
        description: campaign.description || '',
        expectedMessageVolume:
          campaign?.expectedMessageVolume?.toString() || '',
      });
    }
  }, [campaign, reset]);

  const onSubmit = async (values: ProgramDetailsValues) => {
    try {
      onSave({
        description: values.description,
        expectedMessageVolume: parseInt(values.expectedMessageVolume, 10),
      });
    } catch (e) {
      console.error('Failed to save program details:', e);
    }
  };

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          flex: 1,
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '24px',
            height: '100%',
          }}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              gap: '4px',
              width: '100%',
              lineHeight: 'normal',
            }}
          >
            <Box
              as="h2"
              sx={{
                fontSize: '28px',
                fontWeight: 500,
                color: '#19262A',
              }}
            >
              Edit Program Details
            </Box>
            <Box
              sx={{
                fontSize: '16px',
                color: '#4D5659',
              }}
            >
              * Required fields
            </Box>
          </Flex>

          <Flex
            sx={{
              flexDirection: 'column',
              gap: '24px',
              width: '100%',
              flex: 1,
            }}
          >
            <Controller
              name="description"
              control={control}
              rules={{
                validate: {
                  isRequired,
                  isValidInput,
                  withValidMinLength: createMinLengthValidator(40),
                  withValidMaxLength: createMaxLengthValidator(4096),
                },
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextInput
                  required
                  multiline
                  error={!!errors.description}
                  note={errors.description?.message}
                  label="Program Description"
                  maxLength={4096}
                  minRows={5}
                  maxRows={5}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid="programDescriptionInput"
                />
              )}
            />

            <Controller
              name="expectedMessageVolume"
              control={control}
              rules={{
                validate: {
                  isRequired: (val) => !!val?.toString().trim() || 'Required',
                  isPositiveInteger: (val) =>
                    /^\d+$/.test(val?.toString().trim() || '') ||
                    'Must be a positive integer',
                },
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextInput
                  required
                  error={!!errors.expectedMessageVolume}
                  note={errors.expectedMessageVolume?.message}
                  label="Expected Monthly Volume"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  data-testid="expectedMessageVolumeInput"
                />
              )}
            />
          </Flex>
        </Flex>
        <Flex
          sx={{
            justifyContent: 'flex-end',
            gap: '16px',
            width: '100%',
            pt: '24px',
            mt: 'auto',
          }}
        >
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="primary"
            onClick={onCancel}
            disabled={processing}
            type="button"
            data-testid="cancelEditProgramDetails"
          >
            Cancel
          </Button>
          <Button
            size="lg"
            shape="square"
            variant="fill"
            color="primary"
            disabled={!isValid || processing}
            type="submit"
            data-testid="saveEditProgramDetails"
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default ProgramDetailsForm;
